import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_UPSCALE_STATE,
  buildUpscaleRequest,
  hasSourceImage,
  type UpscaleFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import type { StudioParams } from '../../shared/panels/swatchSelection';
import { StudioSwatchChips } from './StudioSwatchChips';
import { ProviderModelPicker } from './ProviderModelPicker';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  /** Upscale has no prompt field; chip row is informational. */
  studioParams?: StudioParams;
  onResult?: (result: OperationResult) => void;
}

const SCALE_FACTORS = [2, 3, 4] as const;

export const UpscalePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  studioParams,
  onResult,
}) => {
  const [state, setState] = useState<UpscaleFormState>(DEFAULT_UPSCALE_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.upscale);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to upscale.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildUpscaleRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ZoomInIcon fontSize="small" />
        <Typography variant="subtitle2">Upscale</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to upscale.</Alert>
      )}

      <StudioSwatchChips studioParams={studioParams} hint="not applied to Upscale" />

      <ProviderModelPicker
        model={state.model}
        onModelChange={m => setState(s => ({ ...s, model: m }))}
        disabled={mutation.isPending || isCreatingSession}
        label="Image model"
        opKind="Upscale"
      />

      <FormControl size="small" fullWidth>
        <InputLabel id="upscale-factor-label">Scale factor</InputLabel>
        <Select
          labelId="upscale-factor-label"
          label="Scale factor"
          value={state.scaleFactor}
          onChange={e => setState(s => ({ ...s, scaleFactor: Number(e.target.value) }))}
        >
          {SCALE_FACTORS.map(f => (
            <MenuItem key={f} value={f}>{`${f}×`}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Typography variant="caption" color="text.secondary">
          Denoise strength · {state.denoiseStrength.toFixed(2)}
        </Typography>
        <Slider
          value={state.denoiseStrength}
          onChange={(_, v) =>
            setState(s => ({ ...s, denoiseStrength: Array.isArray(v) ? v[0] : v }))
          }
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={state.enhanceDetails}
            onChange={e => setState(s => ({ ...s, enhanceDetails: e.target.checked }))}
            size="small"
          />
        }
        label="Enhance fine details"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Upscale failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <ZoomInIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Upscaling…' : 'Upscale'}
        </Button>
      </Box>
    </Stack>
  );
};
